import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import debounce from "lodash/debounce";
import { useSnackbar } from "notistack";

import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import AppBar from "@mui/material/AppBar";
import Tooltip from "@mui/material/Tooltip";
import Toolbar from "@mui/material/Toolbar";
import TableRow from "@mui/material/TableRow";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import TableContainer from "@mui/material/TableContainer";
import { withStyles } from "@mui/styles";

import { styles } from "../../styles/global";
import { isAdmin } from "../../helpers/roles";

import { Dialog } from "../BaseComponents/Dialog";
import { NoResults } from "../BaseComponents/NoResults";
import { AppSkeleton } from "../BaseComponents/Skeleton";

import { copyText, filterFullArray, truncate } from "../../util";
import { ActionCell, ClickToCopy, StyledTableCell, StyledTableRow } from "./styles";
import { createApiKey, getApiKeys, deleteApiKey } from "../../api/apikeys";

function ApikeysTable({ classes }) {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [openTokenDialog, setOpenTokenDialog] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [selectedHashedKey, setSelectedHashedKey] = useState(null);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [selectedApiKeyName, setSelectedApiKeyName] = useState(null);
  const [apiKeyName, setApiKeyName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const searchRef = useRef();
  const isUserAdmin = isAdmin();

  // Skeleton widths for admins and non-admins
  const skeletonWidth = isUserAdmin ? [158, 168, 164, 174, 132] : [112, 192, 202, 200, 214];

  /**
   * Fetch all api keys from the server.
   */
  const fetchTokens = useCallback(async () => {
    setIsLoading(true);
    try {
      const tokens = await getApiKeys();
      setRows(tokens);
      setFilteredRows(tokens);
      setIsEmpty(!tokens.length);
    } catch (error) {
      console.error("Failed to fetch tokens:", error);
      enqueueSnackbar("Failed to fetch tokens", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  /**
   * Close all dialogs.
   */
  const handleCloseDialog = useCallback(() => {
    setApiKeyName("");
    setSelectedHashedKey(null);
    setOpenTokenDialog(false);
  }, []);

  /**
   * Generate a new token.
   */
  const handleGenerateNewToken = useCallback(() => {
    createApiKey(apiKeyName)
      .then((result) => {
        setSelectedApiKey(result);
        setIsCopyDialogOpen(true);
        enqueueSnackbar("API key successfully created");
        fetchTokens();
        if (searchRef.current) {
          searchRef.current.value = "";
        }
        handleCloseDialog();
      })
      .catch(() => {
        enqueueSnackbar("Error generating token", { variant: "error" });
      });
  }, [apiKeyName, enqueueSnackbar, fetchTokens, handleCloseDialog]);

  /**
   * Delete a token.
   */
  const handleDeleteToken = useCallback(() => {
    deleteApiKey({ hashed_key: selectedHashedKey })
      .then(() => {
        enqueueSnackbar("API key successfully deleted");
        setSelectedHashedKey(null);
        fetchTokens();
      })
      .catch(() => {
        enqueueSnackbar("Error deleting api key", { variant: "error" });
      });
  }, [enqueueSnackbar, fetchTokens, selectedHashedKey]);

  /**
   * Filter tokens by query.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleFilter = useCallback(
    debounce((query, rows, setFilteredRows, setIsEmpty) => {
      const newFiltered = filterFullArray(query, rows);
      setFilteredRows(newFiltered);
      setIsEmpty(newFiltered.length === 0);
    }, 200),
    []
  );

  const onFilterChange = useCallback(
    (query) => {
      handleFilter(query, rows, setFilteredRows, setIsEmpty);
    },
    [rows, setFilteredRows, setIsEmpty, handleFilter]
  );

  const handleUpdateKeyName = useCallback((value) => {
    const trimmedValue = value.trim();

    if (value === "" || trimmedValue.length > 0) {
      setApiKeyName(value);
    }
  }, []);

  const handleClipboardCopy = useCallback(() => {
    copyText(selectedApiKey);
    enqueueSnackbar("Copied to clickboard");
  }, [enqueueSnackbar, selectedApiKey]);

  /**
   * Open the dialog to generate a new token.
   */
  const handleOpenTokenDialog = useCallback(() => {
    setOpenTokenDialog(true);
  }, []);

  const deleteButtonProps = useMemo(() => {
    return {
      name: "Delete",
      onAction: handleDeleteToken
    };
  }, [handleDeleteToken]);

  const generateButtonProps = useMemo(() => {
    return {
      name: "Generate",
      onAction: handleGenerateNewToken,
      disabled: !apiKeyName
    };
  }, [apiKeyName, handleGenerateNewToken]);

  const copyApiKeyDialogProps = useMemo(() => {
    return {
      name: "Copy",
      onAction: () => handleClipboardCopy(selectedApiKey),
      disabled: !selectedApiKey
    };
  }, [selectedApiKey, handleClipboardCopy]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  return (
    <>
      <Paper className={classes.paper}>
        <AppBar className={classes.searchBar} position="sticky" color="default" elevation={0}>
          <Toolbar>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <SearchIcon className={classes.block} color="inherit" />
              </Grid>
              <Grid item xs>
                <TextField
                  inputRef={searchRef}
                  fullWidth
                  placeholder="Filter by name"
                  InputProps={{ className: classes.searchInput }}
                  onChange={(e) => onFilterChange(e.target.value)}
                />
              </Grid>
              <Grid item>
                <Button
                  disabled={!isUserAdmin}
                  variant="contained"
                  color="primary"
                  className={classes.addUser}
                  onClick={handleOpenTokenDialog}
                  startIcon={<AddIcon />}
                >
                  Generate New API Key
                </Button>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>

        <div className={classes.contentWrapper}>
          <TableContainer>
            <Table aria-label="api keys table" className={classes.table}>
              <TableHead>
                <TableRow>
                  <StyledTableCell align="center">API Key Name</StyledTableCell>
                  <StyledTableCell align="center">Created By</StyledTableCell>
                  <StyledTableCell align="center">Created At</StyledTableCell>
                  <StyledTableCell align="center">Expires At</StyledTableCell>
                  {isUserAdmin && <StyledTableCell align="center">Actions</StyledTableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {!isEmpty &&
                  !isLoading &&
                  filteredRows.map((row, i) => {
                    return (
                      <StyledTableRow key={`${row.name}-${i}`}>
                        <StyledTableCell align="center">
                          {row.name ?? truncate(row.hashedKey, 30, true)}
                        </StyledTableCell>
                        <StyledTableCell align="center">{row.user}</StyledTableCell>
                        <StyledTableCell align="center">
                          {moment(row.createdAt).format("MM/DD/YYYY") || "-"}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          {moment(row.expireAt).format("MM/DD/YYYY") || "-"}
                        </StyledTableCell>
                        {isUserAdmin && (
                          <ActionCell align="center">
                            <Tooltip title="Delete">
                              <IconButton
                                aria-label="delete"
                                onClick={() => {
                                  setSelectedHashedKey(row.hashedKey);
                                  setSelectedApiKeyName(row.name ?? row.hashedKey);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ActionCell>
                        )}
                      </StyledTableRow>
                    );
                  })}
                {isLoading && <AppSkeleton widths={skeletonWidth} />}
              </TableBody>
            </Table>
            {isEmpty && !isLoading && <NoResults />}
          </TableContainer>
        </div>
      </Paper>

      <Dialog
        title="Generate New API Key"
        onCancel={handleCloseDialog}
        isOpened={openTokenDialog}
        buttonProps={generateButtonProps}
      >
        <TextField
          variant="outlined"
          autoFocus
          margin="dense"
          id="api-key-name"
          label="Key name"
          type="text"
          fullWidth
          onChange={(e) => handleUpdateKeyName(e.target.value)}
          value={apiKeyName}
        />
      </Dialog>

      <Dialog
        buttonProps={copyApiKeyDialogProps}
        onCancel={() => setIsCopyDialogOpen(false)}
        title="Save your API key! You will see it only once."
        isOpened={isCopyDialogOpen}
      >
        <ClickToCopy onClick={() => handleClipboardCopy(selectedApiKey)}>{selectedApiKey}</ClickToCopy>
      </Dialog>

      <Dialog
        title="Delete API Key?"
        isOpened={!!selectedHashedKey}
        buttonProps={deleteButtonProps}
        onCancel={handleCloseDialog}
      >
        {selectedApiKeyName}
      </Dialog>
    </>
  );
}

ApikeysTable.propTypes = {
  classes: PropTypes.object.isRequired,
  organizationId: PropTypes.string.isRequired
};

export default withStyles(styles)(ApikeysTable);
